import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { generatePDF, generateWord } from '../utils/exportHelpers';

const { FiTarget, FiTrendingUp, FiRefreshCw, FiDownload, FiMail, FiAlertTriangle } = FiIcons;

const PublishingResults = ({ responses, scores, recommendedPath, confidence, onRestart }) => {
  const getPathData = (path) => {
    const pathData = {
      Traditional: {
        title: 'Traditional Publishing',
        description: 'Work with an established publisher who handles editing, design, distribution, and marketing in exchange for creative control and lower royalties.',
        scripture: { verse: 'Commit to the Lord whatever you do, and he will establish your plans.', reference: 'Proverbs 16:3' },
        steps: [
          'Research and target 5-10 compatible publishers',
          'Craft a compelling book proposal and query letter',
          'Build platform presence through speaking/social media',
          'Connect with agents or editors at conferences'
        ],
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      },
      Hybrid: {
        title: 'Hybrid Publishing',
        description: 'Partner with a publisher who shares costs and royalties, providing professional services while maintaining author control and faster timelines.',
        scripture: { verse: 'Let all things be done decently and in order.', reference: '1 Corinthians 14:40' },
        steps: [
          'Research hybrid publishers and their contract terms',
          'Prepare manuscript for professional submission',
          'Secure budget for shared publishing costs',
          'Develop marketing plan with publisher partnership'
        ],
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200'
      },
      Indie: {
        title: 'Indie Publishing',
        description: 'Maintain full creative control and maximize royalties while managing all aspects of publishing yourself or with hired help.',
        scripture: { verse: 'Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up.', reference: 'Galatians 6:9' },
        steps: [
          'Research and budget for professional services (editors, designers)',
          'Set up author business and publishing imprint',
          'Plan distribution across multiple platforms',
          'Build launch team and marketing strategy'
        ],
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      }
    };
    return pathData[path] || pathData.Traditional;
  };

  const pathData = getPathData(recommendedPath);
  const confidenceColor = confidence === 'High' ? 'text-green-600' : confidence === 'Moderate' ? 'text-yellow-600' : 'text-orange-600';

  const getRiskFlags = () => {
    const flags = [];
    
    // Traditional flag
    if (responses.platform?.value === 'niche' && responses.outcome?.value === 'trad') {
      flags.push({
        type: 'warning',
        title: 'Platform Consideration',
        message: 'Traditional publishing typically requires a larger platform. Consider building your audience first or exploring hybrid/indie options.',
        suggestion: 'Focus on growing your email list and social media presence while continuing to refine your manuscript.'
      });
    }

    // Hybrid flag
    if (responses.budget?.value === 'low' && responses.bandwidth?.value === 'low') {
      flags.push({
        type: 'warning',
        title: 'Investment Planning',
        message: 'Hybrid publishing requires both budget investment and time commitment. Consider phased indie publishing or coaching options.',
        suggestion: 'Start with developmental editing consultation while saving for production costs.'
      });
    }

    // Indie flag
    if (responses.bandwidth?.value === 'low' && responses.control?.value === 'full') {
      flags.push({
        type: 'warning',
        title: 'Management Reality',
        message: 'Indie publishing with full control requires significant project management. Consider done-for-you services or starting with hybrid.',
        suggestion: 'Research author services companies or build a team of freelancers to handle production tasks.'
      });
    }

    return flags;
  };

  const riskFlags = getRiskFlags();

  const handleExportPDF = async () => {
    try {
      console.log('Starting PDF export for Publishing Path...');
      
      // Build whyFits array more robustly
      const whyFits = [];
      
      if (responses.outcome?.option?.text) {
        whyFits.push(responses.outcome.option.text);
      }
      if (responses.timeline?.option?.text) {
        whyFits.push(responses.timeline.option.text);
      }
      if (responses.control?.option?.text) {
        whyFits.push(responses.control.option.text);
      }
      if (responses.budget?.option?.text) {
        whyFits.push(responses.budget.option.text);
      }

      const content = {
        title: 'Publishing Path Recommendation',
        recommendedPath: recommendedPath,
        confidence: confidence,
        description: pathData.description,
        scores: {
          trad: scores.trad || 0,
          hybrid: scores.hybrid || 0,
          indie: scores.indie || 0
        },
        whyFits: whyFits,
        steps: pathData.steps,
        scripture: pathData.scripture,
        riskFlags: riskFlags,
        date: new Date().toLocaleDateString()
      };

      console.log('Content prepared for PDF:', content);
      await generatePDF(content, 'publishing-path-results.pdf');
      console.log('PDF export completed successfully');
    } catch (error) {
      console.error('Error in PDF export:', error);
      alert('There was an error generating the PDF. Please try again.');
    }
  };

  const handleExportWord = async () => {
    try {
      console.log('Starting Word export for Publishing Path...');
      
      // Build whyFits array more robustly
      const whyFits = [];
      
      if (responses.outcome?.option?.text) {
        whyFits.push(responses.outcome.option.text);
      }
      if (responses.timeline?.option?.text) {
        whyFits.push(responses.timeline.option.text);
      }
      if (responses.control?.option?.text) {
        whyFits.push(responses.control.option.text);
      }
      if (responses.budget?.option?.text) {
        whyFits.push(responses.budget.option.text);
      }

      const content = {
        title: 'Publishing Path Recommendation',
        recommendedPath: recommendedPath,
        confidence: confidence,
        description: pathData.description,
        scores: {
          trad: scores.trad || 0,
          hybrid: scores.hybrid || 0,
          indie: scores.indie || 0
        },
        whyFits: whyFits,
        steps: pathData.steps,
        scripture: pathData.scripture,
        riskFlags: riskFlags,
        date: new Date().toLocaleDateString()
      };

      console.log('Content prepared for Word:', content);
      await generateWord(content, 'publishing-path-results.docx');
      console.log('Word export completed successfully');
    } catch (error) {
      console.error('Error in Word export:', error);
      alert('There was an error generating the Word document. Please try again.');
    }
  };

  const handleEmailResults = () => {
    const subject = encodeURIComponent('My Publishing Path Recommendation');
    const body = encodeURIComponent(`
I just completed the GospelWise Publishing Path Selector!

Recommended Path: ${recommendedPath} (Confidence: ${confidence})

This assessment has given me clarity on my publishing journey. The recommendation fits my goals for ${responses.outcome?.option?.text || 'my book'}.

${pathData.steps.slice(0, 2).join('\n')}
    `);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-4"
        >
          <h1 className="text-4xl font-serif text-gray-900">
            Your Publishing Path Recommendation
          </h1>
          <div className="flex items-center justify-center gap-3">
            <span className={`text-2xl font-semibold ${pathData.color}`}>
              {recommendedPath}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${confidenceColor} bg-opacity-10 ${pathData.bgColor}`}>
              {confidence} Confidence
            </span>
          </div>
        </motion.div>

        {/* Recommendation Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className={`${pathData.bgColor} rounded-2xl shadow-xl p-8 border ${pathData.borderColor}`}
        >
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon icon={FiTarget} className={`text-2xl ${pathData.color}`} />
            <h2 className="text-2xl font-semibold text-gray-900">
              Recommended: {pathData.title}
            </h2>
          </div>
          
          <p className="text-gray-700 leading-relaxed mb-6">
            {pathData.description}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Score Comparison */}
            <div className="bg-white/70 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Score Breakdown</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Traditional:</span>
                  <span className="font-medium">{scores.trad}</span>
                </div>
                <div className="flex justify-between">
                  <span>Hybrid:</span>
                  <span className="font-medium">{scores.hybrid}</span>
                </div>
                <div className="flex justify-between">
                  <span>Indie:</span>
                  <span className="font-medium">{scores.indie}</span>
                </div>
              </div>
            </div>

            {/* Key Factors */}
            <div className="bg-white/70 rounded-xl p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Why This Fits You</h3>
              <ul className="space-y-1 text-sm text-gray-700">
                {responses.outcome?.option && (
                  <li>• {responses.outcome.option.text}</li>
                )}
                {responses.timeline?.option && (
                  <li>• {responses.timeline.option.text}</li>
                )}
                {responses.control?.option && (
                  <li>• {responses.control.option.text}</li>
                )}
                {responses.budget?.option && (
                  <li>• {responses.budget.option.text}</li>
                )}
              </ul>
            </div>
          </div>
        </motion.div>

        {/* Action Steps */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon icon={FiTrendingUp} className="text-2xl text-primary" />
            <h3 className="text-xl font-semibold text-gray-900">
              Your First 3 Faithful Steps (7-14 days)
            </h3>
          </div>
          
          <ol className="space-y-4">
            {pathData.steps.map((step, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </span>
                <span className="text-gray-700">{step}</span>
              </li>
            ))}
          </ol>

          <div className={`mt-6 p-4 rounded-xl ${pathData.bgColor} border ${pathData.borderColor}`}>
            <blockquote className="space-y-2">
              <p className="text-gray-700 italic leading-relaxed">
                "{pathData.scripture.verse}"
              </p>
              <cite className={`font-semibold ${pathData.color}`}>
                — {pathData.scripture.reference}
              </cite>
            </blockquote>
          </div>
        </motion.div>

        {/* Risk Flags */}
        {riskFlags.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiAlertTriangle} className="text-2xl text-yellow-600" />
              <h3 className="text-xl font-semibold text-gray-900">
                Risk & Readiness Notes
              </h3>
            </div>
            
            <div className="space-y-4">
              {riskFlags.map((flag, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">{flag.title}</h4>
                  <p className="text-gray-700 text-sm mb-2">{flag.message}</p>
                  <p className="text-gray-600 text-sm italic">
                    <strong>Suggestion:</strong> {flag.suggestion}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Export Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Save Your Results</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleExportPDF}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-300"
              >
                <SafeIcon icon={FiDownload} className="text-lg" />
                PDF
              </button>
              <button
                onClick={handleExportWord}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-medium transition-all duration-300"
              >
                <SafeIcon icon={FiDownload} className="text-lg" />
                Word
              </button>
            </div>
            <button
              onClick={handleEmailResults}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-300"
            >
              <SafeIcon icon={FiMail} className="text-lg" />
              Email Results
            </button>
          </div>
        </motion.div>

        {/* Restart Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <button
            onClick={onRestart}
            className="inline-flex items-center gap-3 px-6 py-3 bg-accent hover:bg-orange-600 text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiRefreshCw} className="text-lg" />
            Back to Tools
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default PublishingResults;